import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DASHBOARD_BASE } from '../../config/featureFlags';

// Floating entry point to Ask TeamGrid. Graphite pill in both themes so it
// reads as chrome rather than a stray blue balloon, with the coral brand mark
// carrying the only saturation — the glyph is the accent, the surface is not.
const ASK_PATH = `${DASHBOARD_BASE}/ask`;

const FloatingChatButton = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const didDrag = useRef(false);
  const navigate = useNavigate();

  const handleClick = () => {
    if (!didDrag.current) navigate(ASK_PATH);
  };

  return (
    <motion.div
      drag
      dragMomentum={false}
      dragElastic={0}
      onDragStart={() => { didDrag.current = false; setIsDragging(true); }}
      onDrag={(_, info) => {
        if (Math.abs(info.offset.x) > 4 || Math.abs(info.offset.y) > 4) didDrag.current = true;
      }}
      onDragEnd={() => setTimeout(() => setIsDragging(false), 150)}
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 50,
        touchAction: 'none',
        cursor: isDragging ? 'grabbing' : 'grab',
      }}
    >
      <motion.button
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onTouchStart={() => setIsHovered(true)}
        onTouchEnd={() => setTimeout(() => setIsHovered(false), 200)}
        className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#ff584a] touch-manipulation"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isHovered ? 10 : 0,
          height: 52,
          padding: isHovered ? '0 18px 0 12px' : 0,
          width: isHovered ? 'auto' : 52,
          justifyContent: 'center',
          background: 'linear-gradient(145deg, #ff6a52 0%, #f0432f 100%)',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: isHovered
            ? '0 14px 30px -10px rgba(214, 55, 36, 0.55), 0 3px 8px -3px rgba(20, 12, 10, 0.28)'
            : '0 8px 20px -8px rgba(214, 55, 36, 0.45), 0 2px 5px -2px rgba(20, 12, 10, 0.22)',
          transition: 'padding 240ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 200ms ease',
          WebkitTapHighlightColor: 'transparent',
          userSelect: 'none',
          cursor: 'inherit',
        }}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.97, y: 0 }}
        aria-label="Ask TeamGrid"
      >
        <Sparkles size={20} strokeWidth={2.2} color="#ffffff" style={{ flexShrink: 0 }} />

        <motion.span
          initial={false}
          animate={{ width: isHovered ? 'auto' : 0, opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: '0.01em',
            color: '#ffffff',
          }}
        >
          Ask TeamGrid
        </motion.span>
      </motion.button>
    </motion.div>
  );
};

export default FloatingChatButton;
